import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import {
  getCategory,
  getLocation,
  listingsByCategoryAndLocation,
  categories,
  locations,
  comboHref,
  listingHref,
} from "@/lib/data";
import { Star, MapPin, ArrowRight, Check } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/location/$loc/$cat/")({
  loader: ({ params }) => {
    const loc = getLocation(params.loc);
    const cat = getCategory(params.cat);
    if (!loc || !cat) throw notFound();
    return { loc, cat };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { loc, cat } = loaderData;
    const title = `Best ${cat.name} in ${loc.name}, ${loc.state} | LetsListBusiness`;
    const description = `Discover top-rated ${cat.name.toLowerCase()} in ${loc.name}. Verified listings, real reviews and recommendations.`;
    const url = `${SITE_URL}/location/${loc.slug}/${cat.slug}/`;
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: cat.image },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: loc.name, href: `/location/${loc.slug}/` },
          { label: cat.name, href: url },
        ], SITE_URL),
      ],
    };
  },
  component: ComboPage,
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Page not found</h1>
      <Link to="/location" className="mt-4 inline-block text-primary">Browse cities</Link>
    </div>
  ),
});

function ComboPage() {
  const { loc, cat } = Route.useLoaderData();
  const items = listingsByCategoryAndLocation(cat.slug, loc.slug);
  const otherCats = categories.filter((c) => c.slug !== cat.slug);
  const otherLocs = locations.filter((l) => l.slug !== loc.slug);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Locations", href: "/location/" },
          { label: loc.name, href: `/location/${loc.slug}/` },
          { label: cat.name },
        ]}
      />

      {/* Editorial header strip */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto grid gap-6 px-4 py-12 lg:grid-cols-12 lg:py-16">
          <div className="lg:col-span-8">
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-accent">
              {cat.icon} {cat.name} · {loc.name}, {loc.state}
            </div>
            <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] sm:text-6xl">
              The {items.length} best {cat.name.toLowerCase()} in {loc.name}
            </h1>
          </div>
          <p className="text-secondary-foreground/80 lg:col-span-4 lg:pt-10">
            Curated by locals. Ranked by ratings, review count and the kind of
            word-of-mouth that only neighbors can give.
          </p>
        </div>
      </section>

      {/* Ranked list */}
      <section className="container mx-auto px-4 py-12">
        <ol className="space-y-4">
          {items.map((l, i) => (
            <li key={l.id}>
              <Link
                {...listingHref(l)}
                className="group grid grid-cols-12 items-center gap-4 overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-lg sm:gap-6 sm:p-4"
              >
                <div className="col-span-2 flex items-center justify-center sm:col-span-1">
                  <span className="font-display text-3xl font-bold text-primary sm:text-5xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="col-span-10 sm:col-span-3">
                  <div className="overflow-hidden rounded-xl">
                    <img src={l.image} alt={l.name} loading="lazy" className="aspect-4/3 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <h2 className="font-display text-xl font-bold sm:text-2xl">{l.name}</h2>
                  <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1 font-semibold text-foreground">
                      <Star className="h-3.5 w-3.5 fill-accent stroke-accent" />
                      {l.rating}
                    </span>
                    <span>·</span>
                    <span>{l.reviews} reviews</span>
                    <span>·</span>
                    <span>{l.priceRange}</span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{l.description}</p>
                  <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {l.address}
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-2 sm:text-right">
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
                    View <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ol>
        {items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <p className="text-muted-foreground">No {cat.name.toLowerCase()} listed in {loc.name} yet.</p>
          </div>
        )}
      </section>

      {/* Local Insights — City + Category specific content */}
      <section className="bg-card border-y border-border py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">Navigating the {cat.name.toLowerCase()} scene in {loc.name}.</h2>
            <div className="mt-8 grid gap-8 md:grid-cols-2">
              <div className="space-y-4">
                <p className="text-lg leading-relaxed text-muted-foreground">
                  The {cat.name.toLowerCase()} landscape in {loc.name} is shaped by the city's unique {loc.state} culture. From the busiest streets to hidden alleyways, we've vetted each of these locations to ensure they meet our strict quality standards.
                </p>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Our selection process for {cat.name.toLowerCase()} in {loc.name} involves both data analysis (ratings and review volume) and personal visits by our editorial team to confirm the vibe and service quality.
                </p>
              </div>
              <div className="rounded-3xl border border-border bg-surface p-8 shadow-sm">
                 <h3 className="font-display text-xl font-bold">Local Knowledge</h3>
                 <dl className="mt-4 space-y-4">
                   {[
                     { l: "Primary Hub", v: "Downtown & Surrounds" },
                     { l: "Price Range", v: "Diverse" },
                     { l: "Best Time", v: "Weekdays for less crowd" },
                   ].map((item) => (
                     <div key={item.l} className="flex justify-between border-b border-border pb-2 text-sm">
                       <dt className="text-muted-foreground">{item.l}</dt>
                       <dd className="font-bold">{item.v}</dd>
                     </div>
                   ))}
                 </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-link footers — two columns */}
      <section className="border-t border-border bg-surface py-14">
        <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-xl font-bold">More categories in {loc.name}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {otherCats.map((c) => (
                <Link
                  key={c.slug}
                  {...comboHref(loc.slug, c.slug)}
                  className="rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium hover:bg-primary hover:text-primary-foreground"
                >
                  {c.icon} {c.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">{cat.name} in other cities</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {otherLocs.map((l) => (
                <Link
                  key={l.slug}
                  {...comboHref(l.slug, cat.slug)}
                  className="rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium hover:bg-primary hover:text-primary-foreground"
                >
                  {cat.name} in {l.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article — Internal Linking */}
      <section className="container mx-auto px-4 py-24">
        <div className="rounded-[3rem] border border-border bg-card p-1 items-center grid lg:grid-cols-2">
           <div className="p-8 sm:p-12 lg:p-16">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">City Insight</div>
              <h2 className="mt-6 font-display text-4xl font-bold leading-tight">Beyond the listings: Our deep dive into {loc.name} culture.</h2>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                Want to know more about what makes {loc.name} tick? Our latest blog post explores the hidden history and cultural shifts that have made the city's {cat.name.toLowerCase()} scene what it is today.
              </p>
              <Link to="/blog" className="mt-8 inline-flex items-center justify-center rounded-full bg-foreground px-8 py-4 text-sm font-bold text-background transition-transform hover:scale-105">
                Read the blog
              </Link>
           </div>
           <div className="hidden lg:block h-full">
              <img 
                src={cat.image} 
                alt={cat.name} 
                className="h-full w-full object-cover rounded-[2.8rem]"
              />
           </div>
        </div>
      </section>
    </>
  );
}
